import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiUnlock, FiX, FiPlus, FiCheckCircle, FiSlash } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = () => ({ period: '', reason: '' });

const PeriodReopenRequests = () => {
  const { isChairman, user } = useAuth();
  const isCfo = user?.role?.toUpperCase() === 'CFO';
  const [entities, setEntities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, perRes, reqRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.periods.list(), financeAPI.periodReopenRequests.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setPeriods(perRes.data.results || perRes.data || []);
      setRequests(reqRes.data.results || reqRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load period reopen requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.periodReopenRequests.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to submit reopen request.');
    } finally {
      setSaving(false);
    }
  };

  const approve = async (req, as) => {
    const code = window.prompt('This is a high-risk action — enter your current authenticator code to confirm:');
    if (!code) return;
    setBusyId(req.id);
    setError(null);
    try {
      if (as === 'chairman') await financeAPI.periodReopenRequests.approveChairman(req.id, code);
      else await financeAPI.periodReopenRequests.approveCfo(req.id, code);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Approval failed.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (req) => {
    const code = window.prompt('This is a high-risk action — enter your current authenticator code to confirm rejection:');
    if (!code) return;
    setBusyId(req.id);
    setError(null);
    try {
      await financeAPI.periodReopenRequests.reject(req.id, code);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reject.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || `#${id}`;
  const periodLabel = (id) => { const p = periods.find((x) => x.id === id); return p ? `${entityName(p.entity)} — ${p.name}` : `#${id}`; };
  const lockedPeriods = periods.filter((p) => p.state === 'LOCKED');

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiUnlock size={22} />Period Reopen Requests</h1>
          <p style={fs.pageSubtitle}>Reopening a locked fiscal period requires BOTH the Chairman and the CFO to approve — the period only transitions back to OPEN once both are in.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Request</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><div style={fs.label}>Locked Period</div>
            <select required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{lockedPeriods.map((p) => <option key={p.id} value={p.id}>{entityName(p.entity)} — {p.name}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={fs.label}>Reason (required)</div>
            <textarea required rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={{ ...fs.input, width: '100%', resize: 'vertical' }} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Submitting…' : 'Submit Request'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={fs.emptyState}><FiUnlock size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No reopen requests yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {requests.map((req) => {
            const busy = busyId === req.id;
            const color = fs.STATUS_COLORS[req.status] || '#888';
            return (
              <div key={req.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{periodLabel(req.period)}</span>
                      <span style={fs.badge(color)}>{req.status}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>requested {new Date(req.requested_at).toLocaleString()}</div>
                    <div style={{ color: fs.colors.textDim, fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>&ldquo;{req.reason}&rdquo;</div>
                    {req.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11 }}>
                        <span style={{ color: req.chairman_approved_by ? fs.colors.green : fs.colors.textFaint }}>{req.chairman_approved_by ? '✓' : '○'} Chairman sign-off</span>
                        <span style={{ color: req.cfo_approved_by ? fs.colors.green : fs.colors.textFaint }}>{req.cfo_approved_by ? '✓' : '○'} CFO sign-off</span>
                      </div>
                    )}
                  </div>
                  {req.status === 'PENDING' && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {isChairman() && !req.chairman_approved_by && (
                        <button onClick={() => approve(req, 'chairman')} disabled={busy} style={fs.successButton}><FiCheckCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Approve (Chairman)</button>
                      )}
                      {isCfo && !req.cfo_approved_by && (
                        <button onClick={() => approve(req, 'cfo')} disabled={busy} style={fs.successButton}><FiCheckCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Approve (CFO)</button>
                      )}
                      <button onClick={() => reject(req)} disabled={busy} style={fs.dangerButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Reject</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PeriodReopenRequests;
